import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../@types/RootState';
import { ICompetitorPriceSources, removeUnavailableCompetitorPriceSources } from '../../pages/IPA/Entities';

export const initialChangedCostsDays = 7;
export const initialChangedCostsMinPct = 0;
export const MAX_CHANGED_COSTS_MIN_PCT = 99.9;
export const MAX_CHANGED_COSTS_DAYS = 365;

type Operators = 'BETWEEN' | 'HIGHEST' | 'LOWEST';
export type QuickActionPriceTypeFilter = 'REGULAR' | 'OFFER';

export const ADJUSTED_PRICE_BY_LIMIT = 'ADJUSTED_PRICE_BY_LIMIT';
export type LimitFilterValue = boolean | typeof ADJUSTED_PRICE_BY_LIMIT;

type BrokenLimit = {
  isActive: boolean;
  value: LimitFilterValue;
};

type ChangedPrices = {
  isActive: boolean;
};

type CompetitorPrices = {
  isActive: boolean;
};

type IsProductMaster = {
  isActive: boolean;
};

type MinDateCpi = {
  isActive: boolean;
  value: number;
  competitorPriceSources: ICompetitorPriceSources[];
};

export type MarginFilterProps = {
  isActive: boolean;
  value: number;
  minValue: number;
  maxValue: number;
  operator: Operators;
  priceTypeFilter: 'SUGGESTED' | 'CURRENT' | null;
};
export type CompetitivenessFilterProps = {
  isActive: boolean;
  value: number;
  minValue: number;
  maxValue: number;
  operator: Operators;
  priceTypeFilter: 'SUGGESTED' | 'CURRENT' | null;
};

export type PriceVariationValueFilterType = 'POSITIVE' | 'NEGATIVE';
export type PriceVariationFilterProps = {
  isActive: boolean;
  value: number;
  minValue: number;
  maxValue: number;
  operator: Operators;
  valueFilterType: PriceVariationValueFilterType[];
};

export type ChangedCostRequest = {
  costChangeDays: number;
  costChangeMinPct: number;
  directionUp: boolean;
  directionDown: boolean;
};

export type ChangedCostsFilter = {
  isActive: boolean;
  value: number;
  minPct: number;
  directionUp: boolean;
  directionDown: boolean;
};

export const clampMinPct = (value: number): number => {
  if (isNaN(value) || value < initialChangedCostsMinPct) return initialChangedCostsMinPct;

  return Math.min(value, MAX_CHANGED_COSTS_MIN_PCT);
};

export const parseDays = (days: number | string | null | undefined): number => {
  if (days === '' || days === null || days === undefined) return initialChangedCostsDays;

  const parsed = Number(days);

  if (isNaN(parsed) || parsed < 0) return initialChangedCostsDays;

  return Math.min(parsed, MAX_CHANGED_COSTS_DAYS);
};

export const normalizeChangedCostsFilter = (filter: ChangedCostsFilter): ChangedCostsFilter => {
  const hasDirection = filter.directionUp || filter.directionDown;

  return {
    ...filter,
    value: parseDays(filter.value),
    minPct: clampMinPct(filter.minPct),
    directionUp: hasDirection ? filter.directionUp : true,
    directionDown: hasDirection ? filter.directionDown : true,
  };
};

type StockQuantityFilter = {
  isActive: boolean;
  value: number;
};

type OfferDataFilter = {
  isActive: boolean;
};

export type PriceManagerQuickActionFilter = {
  brokenLimit: BrokenLimit;
  changedPrices: ChangedPrices;
  competitorPrices: CompetitorPrices;
  minDateCompetitorPrice: MinDateCpi;
  isProductMaster: IsProductMaster;
  marginRange: MarginFilterProps;
  priceVariationRange: PriceVariationFilterProps;
  cpiRange: CompetitivenessFilterProps;
  changedCosts: ChangedCostsFilter;
  stockQuantityFilter: StockQuantityFilter;
  isOfferData: OfferDataFilter;
  priceTypeFilterIsActive: boolean;
  priceTypeFilter: QuickActionPriceTypeFilter;
  offerDataAutoEnabledByPriceTypeFilter: boolean;
};

export type ChangeQuickActionFilterValue = {
  name: 'brokenLimit' | 'minDateCompetitorPrice';
  value: LimitFilterValue | number;
};

export type QuickActionFilterLimitsKeys = Exclude<
  keyof PriceManagerQuickActionFilter,
  'priceTypeFilter' | 'priceTypeFilterIsActive' | 'offerDataAutoEnabledByPriceTypeFilter'
>;

const initialState: PriceManagerQuickActionFilter = {
  brokenLimit: {
    isActive: false,
    value: false,
  },
  changedPrices: {
    isActive: false,
  },
  competitorPrices: {
    isActive: false,
  },
  minDateCompetitorPrice: {
    isActive: false,
    value: 7,
    competitorPriceSources: [],
  },
  isProductMaster: {
    isActive: false,
  },
  marginRange: {
    isActive: false,
    value: 0,
    minValue: 0,
    maxValue: 0,
    operator: 'HIGHEST',
    priceTypeFilter: 'SUGGESTED',
  },
  priceVariationRange: {
    isActive: false,
    value: 0,
    minValue: 0,
    maxValue: 0,
    operator: 'HIGHEST',
    valueFilterType: ['POSITIVE', 'NEGATIVE'],
  },
  cpiRange: {
    isActive: false,
    value: 0,
    minValue: 0,
    maxValue: 0,
    operator: 'HIGHEST',
    priceTypeFilter: 'SUGGESTED',
  },
  changedCosts: {
    isActive: false,
    value: initialChangedCostsDays,
    minPct: initialChangedCostsMinPct,
    directionUp: true,
    directionDown: true,
  },
  isOfferData: {
    isActive: false,
  },
  stockQuantityFilter: {
    isActive: false,
    value: 0,
  },
  priceTypeFilterIsActive: false,
  priceTypeFilter: 'REGULAR',
  offerDataAutoEnabledByPriceTypeFilter: false,
};

const saveFilterSessionStorage = (key: string, state: MarginFilterProps | PriceVariationFilterProps) => {
  sessionStorage.setItem(`gerenciador-${key}`, JSON.stringify(state));
};

const slice = createSlice({
  name: 'filtros-state',
  initialState,
  reducers: {
    TOGGLE_QUICK_FILTER_ON(state, action: PayloadAction<QuickActionFilterLimitsKeys>) {
      state[action.payload].isActive = true;
    },
    toggleQuickFilterOff(state, action: PayloadAction<{ name: QuickActionFilterLimitsKeys }>) {
      state[action.payload.name].isActive = false;
    },
    toggleQuickFilter(state, action: PayloadAction<{ name: QuickActionFilterLimitsKeys }>) {
      state[action.payload.name].isActive = !state[action.payload.name].isActive;
    },
    CHANGE_QUICK_FILTER_VALUE(state, action: PayloadAction<ChangeQuickActionFilterValue>) {
      state[action.payload.name].value = action.payload.value;
    },
    togglePriceCPI(state) {
      state.competitorPrices.isActive = !state.competitorPrices.isActive;
    },
    RESET_QUICK_ACTION_FILTER() {
      saveFilterSessionStorage('marginFilter', initialState.marginRange);
      return initialState;
    },
    toggleQuickActionFilter(state, action: PayloadAction<{ key: QuickActionFilterLimitsKeys }>) {
      state[action.payload.key].isActive = !state[action.payload.key].isActive;

      if (action.payload.key === 'isOfferData') {
        state.offerDataAutoEnabledByPriceTypeFilter = false;
      }
    },
    setQuickActionPriceTypeFilter(state, action: PayloadAction<QuickActionPriceTypeFilter>) {
      state.priceTypeFilterIsActive = true;
      state.priceTypeFilter = action.payload;

      if (action.payload === 'OFFER') {
        state.offerDataAutoEnabledByPriceTypeFilter = !state.isOfferData.isActive;
        state.isOfferData.isActive = true;
        return;
      }

      if (state.offerDataAutoEnabledByPriceTypeFilter) {
        state.isOfferData.isActive = false;
      }

      state.offerDataAutoEnabledByPriceTypeFilter = false;
    },
    toggleQuickActionPriceTypeFilter(state) {
      state.priceTypeFilterIsActive = !state.priceTypeFilterIsActive;

      if (state.priceTypeFilterIsActive) {
        state.priceTypeFilter = 'REGULAR';
        if (state.offerDataAutoEnabledByPriceTypeFilter) {
          state.isOfferData.isActive = false;
          state.offerDataAutoEnabledByPriceTypeFilter = false;
        }
        return;
      }

      if (state.offerDataAutoEnabledByPriceTypeFilter) {
        state.isOfferData.isActive = false;
      }

      state.offerDataAutoEnabledByPriceTypeFilter = false;
    },
    setMinDateCompetitorPriceSource(state, action: PayloadAction<ICompetitorPriceSources[]>) {
      state.minDateCompetitorPrice.competitorPriceSources = removeUnavailableCompetitorPriceSources(action.payload);
    },
    setMarginFilter(state, action: PayloadAction<MarginFilterProps>) {
      state.marginRange = action.payload;
      saveFilterSessionStorage('margin-filter', action.payload);
    },
    setPriceVariationFilter(state, action: PayloadAction<PriceVariationFilterProps>) {
      state.priceVariationRange = action.payload;
      saveFilterSessionStorage('price-variation-filter', action.payload);
    },
    setCompetitivenessFilter(state, action: PayloadAction<CompetitivenessFilterProps>) {
      state.cpiRange = action.payload;
      saveFilterSessionStorage('competitiveness-filter', action.payload);
    },
    setChangedCostsFilter(state, action: PayloadAction<ChangedCostsFilter>) {
      state.changedCosts = normalizeChangedCostsFilter(action.payload);
    },
    toggleChangedCostsIsActive(state) {
      state.changedCosts.isActive = !state.changedCosts.isActive;
    },
    setChangedCostsIsActive(state, action: PayloadAction<boolean>) {
      state.changedCosts.isActive = action.payload;
    },
    setStockQuantityValue(state, action: PayloadAction<number>) {
      state.stockQuantityFilter.value = action.payload;
    },
    toggleStockQuantityIsActive(state) {
      state.stockQuantityFilter.isActive = !state.stockQuantityFilter.isActive;
    },
    setStockQuantityIsActive(state, action: PayloadAction<boolean>) {
      state.stockQuantityFilter.isActive = action.payload;
    },
  },
});

const selectorQuickActionFilters = (state: RootState) => {
  return state.gerenciadorPrecosReducer.quickActionsFilters;
};

const selectorChangedCosts = (state: RootState) => {
  return state.gerenciadorPrecosReducer.quickActionsFilters.changedCosts;
};

const selectorStockQuantityFilter = (state: RootState) => {
  return state.gerenciadorPrecosReducer.quickActionsFilters.stockQuantityFilter;
};

const {
  TOGGLE_QUICK_FILTER_ON,
  toggleQuickFilterOff,
  toggleQuickFilter,
  CHANGE_QUICK_FILTER_VALUE,
  RESET_QUICK_ACTION_FILTER,
  togglePriceCPI,
  toggleQuickActionFilter,
  setMinDateCompetitorPriceSource,
  setMarginFilter,
  setPriceVariationFilter,
  setCompetitivenessFilter,
  setQuickActionPriceTypeFilter,
  toggleQuickActionPriceTypeFilter,
  setChangedCostsFilter,
  toggleChangedCostsIsActive,
  setChangedCostsIsActive,
  setStockQuantityValue,
  toggleStockQuantityIsActive,
  setStockQuantityIsActive,
} = slice.actions;

export {
  CHANGE_QUICK_FILTER_VALUE,
  RESET_QUICK_ACTION_FILTER,
  selectorChangedCosts,
  selectorQuickActionFilters,
  selectorStockQuantityFilter,
  setChangedCostsFilter,
  setChangedCostsIsActive,
  setCompetitivenessFilter,
  setMarginFilter,
  setMinDateCompetitorPriceSource,
  setPriceVariationFilter,
  setQuickActionPriceTypeFilter,
  setStockQuantityIsActive,
  setStockQuantityValue,
  TOGGLE_QUICK_FILTER_ON,
  toggleChangedCostsIsActive,
  togglePriceCPI,
  toggleQuickActionFilter,
  toggleQuickActionPriceTypeFilter,
  toggleQuickFilter,
  toggleQuickFilterOff,
  toggleStockQuantityIsActive,
};

export default slice.reducer;
